import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, setAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, HardHat } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState(null);

  useEffect(() => { api.get("/branding").then((r) => setBranding(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(path, body);
      setAuthToken(data.token);
      setUser(data);
      navigate(data.role === "trabajador" ? "/mi-ficha" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };



  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 grid-texture relative overflow-hidden">
        <div className="flex items-center gap-3 relative z-10">
          {branding?.logo ? (
            <img src={branding.logo} alt={branding.company_name || "Logo"}
              className="h-16 w-auto max-w-[260px] rounded-lg bg-white object-contain px-3 py-2" />
          ) : (
            <>
              <div className="h-11 w-11 rounded-lg bg-accent flex items-center justify-center">
                <HardHat className="h-6 w-6 text-white" />
              </div>
              <span className="font-display font-bold text-xl">Control de Personal</span>
            </>
          )}
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            Gestión integral de tu plantilla y prevención de riesgos.
          </h1>
          <p className="text-slate-300 text-base max-w-md">
            DNI, reconocimientos médicos, cursos PRL, EPIs, herramientas y flota de vehículos.
            Controla caducidades y realiza copias de seguridad en un solo lugar.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-slate-400 text-sm">
          <ShieldCheck className="h-4 w-4" /> Datos protegidos · Copias de seguridad automáticas
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {branding?.logo ? (
              <img src={branding.logo} alt={branding.company_name || "Logo"} className="h-11 w-auto max-w-[180px] object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center">
                <HardHat className="h-5 w-5 text-white" />
              </div>
            )}
            {!branding?.logo && <span className="font-display font-bold text-lg">Control de Personal</span>}
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">
            {mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
          </h2>
          <p className="text-muted-foreground text-sm mt-1 mb-6">
            {mode === "login" ? "Accede a tu panel de control" : "Regístrate como usuario (solo lectura)"}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Nombre</Label>
                <Input id="name" data-testid="register-name-input" value={name}
                  onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.es" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" required data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
            )}

            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={busy}
              data-testid="login-submit-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Entrar" : "Crear cuenta"}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Las cuentas las crea el administrador en Ajustes → Usuarios y accesos.
          </p>
          {branding?.lopd_text && (
            <details className="mt-8 border-t pt-4" data-testid="login-lopd">
              <summary className="cursor-pointer text-xs font-medium uppercase tracking-wider text-slate-500 hover:text-slate-700">
                Protección de datos (LOPD / RGPD)
              </summary>
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">{branding.lopd_text}</p>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
